
import { Component, Vue } from 'vue-property-decorator';
import { mixins } from 'vue-class-component';
import { namespace } from 'vuex-class';
import { ValidationObserver } from 'vee-validate';
import VWrapper from '@/components/shared/VWrapper.vue';
import EnrollmentsTable from '@/components/enrollments/EnrollmentsTable.vue';
import Notification from '@/mixins/Notification';
import Filter from '@/interfaces/api/Filter';
import { Enrollment as EnrollmentsModel } from '@/interfaces/models/Enrollment';
import { Venue } from '@/interfaces/models/Venue';
import { Permission } from '@/enums/Permission';
import { getVenuesFromCookie } from '@/util/helper';
import AuthApiService from '@/api/http/AuthApiService';

const app = namespace('app');
const enrollments = namespace('enrollments');

@Component({
  components: { VWrapper, EnrollmentsTable, ValidationObserver },
})
export default class Enrollments extends mixins(Notification) {
  @enrollments.Action('setFilter') public setFilter!: (filter: Filter) => void;
  @enrollments.Action('fetch') public getItems!: any;

  @enrollments.State((state) => state.pagination.total) public total!: number;
  @enrollments.State('filter') public filter!: Filter;
  @enrollments.State('items') public items!: EnrollmentsModel[];
  @app.State('venues') public venues!: Venue[];

  public $refs!: {
    observer: InstanceType<typeof ValidationObserver>;
  };

  public editDialog: { isVisible: boolean; enrollmentId: string; comment?: string; type: 'accept' | 'reject' | '' } = {
    isVisible: false,
    enrollmentId: '',
    comment: '',
    type: '',
  };

  get venuesFiltered(): Venue[] {
    if (this.$isAdmin()) {
      return this.venues;
    }

    const userVenuesList: string[] = getVenuesFromCookie();
    const userVenues: Venue[] = this.venues.filter((venue: Venue) => (userVenuesList || []).includes(venue._id));

    return userVenues || [];
  }

  public async mounted() {
    this.$startLoading('enrollments');
    await this.setFilter({});
    await this.getItems({ page: 1 });
    this.$stopLoading('enrollments');
  }

  public async onFilter(val: object) {
    this.$startLoading('enrollments');
    this.setFilter(val);
    await this.getItems({ page: 1 });
    this.$stopLoading('enrollments');
  }

  public openDialog(id: string, type: 'accept' | 'reject') {
    if (
      !id ||
      (type === 'accept' && !this.$can(Permission.OAUTH_ENROLLMENT_ACCEPT)) ||
      (type === 'reject' && !this.$can(Permission.OAUTH_ENROLLMENT_REJECT))
    ) {
      return;
    }

    this.editDialog.type = type;
    this.editDialog.enrollmentId = id;
    this.editDialog.isVisible = true;

    this.$startLoading(`enrollments.${type}`);
  }

  public async respondToEnrollment() {
    this.$refs.observer.validate().then(async (res: boolean) => {
      if (res) {
        try {
          const api: AuthApiService = new AuthApiService();
          const data: {
            id: string;
            comment: string;
          } = { id: this.editDialog.enrollmentId, comment: this.editDialog.comment || '' };

          this.$startLoading('enrollments.respond');

          if (this.editDialog.type === 'accept') {
            await api.acceptEnrollment(data);
          }

          if (this.editDialog.type === 'reject') {
            await api.rejectEnrollment(data);
          }

          this.stopEditing();
          this.refreshData();
        } catch (e) {
          throw e;
        } finally {
          this.$stopLoading('enrollments.respond');
        }
      } else {
        this.notifyError('notification.form');
      }
    });
  }

  public async refreshData() {
    this.$startLoading('enrollments');
    await this.getItems({ page: this.$route.query.page || 1 });
    this.$stopLoading('enrollments');
  }

  public stopEditing() {
    this.$stopAllLoading();
    this.editDialog.isVisible = false;
    this.editDialog.enrollmentId = '';
    this.editDialog.comment = '';
    this.editDialog.type = '';
  }
}
